import { useEffect, useState } from 'react';
import { api } from '../../lib/api';

export default function Scenarios({ isAdmin }: { isAdmin: boolean }) {
  const [scenarios, setScenarios] = useState<any[] | null>(null);
  const [stats, setStats] = useState<Record<string, any>>({});
  const [busy, setBusy] = useState<string | null>(null);

  async function load() {
    const [s, st] = await Promise.all([api.adminScenarios(), api.adminScenarioStats()]);
    setScenarios(s.scenarios);
    const map: Record<string, any> = {};
    for (const r of st.scenarios) map[r.id] = r;
    setStats(map);
  }
  useEffect(() => { load(); }, []);

  async function publish(id: string) { setBusy(id); try { await api.adminPublishScenario(id); await load(); } finally { setBusy(null); } }
  async function retire(id: string) { setBusy(id); if (!confirm('Retire this scenario? Learners will no longer see it.')) { setBusy(null); return; } try { await api.adminRetireScenario(id); await load(); } finally { setBusy(null); } }

  return (
    <div>
      <h1 className="text-2xl font-semibold mb-4">Scenario library</h1>
      <p className="text-mono-white/50 text-sm mb-4">
        Authored via Claude Code CLI sessions on the server (<code className="font-mono text-mono-cyan">tools/seed-scenarios.ts</code>). Promote a draft to <span className="text-mono-green">published</span> only after clinical sign-off.
      </p>

      {scenarios == null && <p className="text-mono-white/60">Loading…</p>}
      {scenarios && (
        <div className="space-y-3">
          {scenarios.map((s) => {
            const st = stats[s.id] || {};
            return (
              <div key={s.id} className="panel p-4">
                <div className="flex justify-between items-start gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold">{s.title}</h3>
                      <StatusBadge status={s.status} />
                      <span className="chip-off uppercase text-[10px] tracking-widest">{s.difficulty}</span>
                      <span className="chip-off font-mono text-[10px]">{s.region}</span>
                    </div>
                    {isAdmin && (
                      <p className="text-xs text-mono-white/60 mt-2 italic">
                        Hidden cause: <span className="text-mono-white/85">{s.hidden_diagnosis || '—'}</span>
                      </p>
                    )}
                    <p className="text-[10px] text-mono-white/40 font-mono mt-1">
                      {s.source_seed} · {s.published_at ? `published ${new Date(s.published_at).toLocaleDateString()}` : 'never published'}
                    </p>
                  </div>
                  <div className="text-right text-xs text-mono-white/70 shrink-0">
                    <div>Plays: <span className="font-mono text-mono-white">{st.play_count ?? 0}</span></div>
                    <div>Avg score: <span className="font-mono text-mono-white">{st.avg_score != null ? Number(st.avg_score).toFixed(0) : '—'}</span></div>
                    <div>Avg CCF: <span className="font-mono text-mono-white">{st.avg_ccf_pct != null ? Number(st.avg_ccf_pct).toFixed(0) + '%' : '—'}</span></div>
                  </div>
                  {isAdmin && (
                    <div className="flex flex-col gap-2 shrink-0">
                      {s.status !== 'published' && <button onClick={() => publish(s.id)} disabled={busy === s.id} className="btn-primary text-xs disabled:opacity-50">Publish</button>}
                      {s.status !== 'retired' && <button onClick={() => retire(s.id)} disabled={busy === s.id} className="btn-ghost text-xs disabled:opacity-50">Retire</button>}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    draft: 'bg-alarm-amber/20 text-alarm-amber',
    review: 'bg-mono-cyan/20 text-mono-cyan',
    published: 'bg-mono-green/20 text-mono-green',
    retired: 'bg-ink-800 text-mono-white/40',
  };
  return <span className={`chip ${colors[status] || 'chip-off'} uppercase text-[10px] tracking-widest`}>{status}</span>;
}
